import { useAtomValue } from 'jotai';

import { useTranslate } from '@growtherapy/i18n';
import { Body, Button, Link, cn } from '@growtherapy/sprout-ui';

import { useTheme } from '../../hooks';
import { useVideoPlatform } from '../../hooks/useVideoPlatform';
import { Translate } from '../../i18n/Translate';
import { AudioControlWrapper as AudioControl } from '../../session/controls/AudioControl';
import { VideoControlWrapper as VideoControl } from '../../session/controls/VideoControl';
import { Theme } from '../../state';
import { LocalMediaStatus, VideoPlatform, localAudioContextAtom, localVideoContextAtom } from '../../video-sdk';
import { permissionDeniedAtom } from '../../video-sdk/twilio';
import { BlockedPermissionsActionItemWrapper as BlockedPermissionsActionItem } from './BlockedPermissionsActionItem';
import { HelpModalItem } from './HelpModalItem';

const CLIENT_AUDIO_VIDEO_TROUBLESHOOT_ARTICLE =
  'https://help.growtherapy.com/clients/en/articles/8482385-grow-therapy-telehealth-faq#h_f12c851172';

type AudioVideoTroubleshootActionItemProps = {
  permissionIsBlocked: boolean;
  isDarkTheme: boolean;
  isVideoEnabled?: boolean;
  isAudioEnabled?: boolean;
  onOpenSettings?: () => void;
  shouldShowVideoQualitySettings?: boolean;
};

export function AudioVideoTroubleshootActionItem({
  permissionIsBlocked,
  isDarkTheme,
  isVideoEnabled,
  isAudioEnabled,
  onOpenSettings,
  shouldShowVideoQualitySettings = true,
}: AudioVideoTroubleshootActionItemProps) {
  const t = useTranslate();

  if (permissionIsBlocked) {
    return <BlockedPermissionsActionItem />;
  }

  return (
    <>
      <HelpModalItem
        heading={t('telehealth-core:troubleshooting.myCameraAndMicArentWorking')}
        description={
          <div className='flex flex-col gap-4'>
            <Body.Default className={cn('text-neutral-800', { 'rebrand:text-text-inverse': isDarkTheme })}>
              {t('telehealth-core:troubleshooting.makeSureYourCameraAndMicrophoneAreOn')}
            </Body.Default>
            <div className='grid md:grid-cols-3 grid-cols-2'>
              <div className='flex flex-row items-center gap-4'>
                <VideoControl />
                <Body.Default className={cn({ 'rebrand:text-text-inverse': isDarkTheme })}>
                  {isVideoEnabled
                    ? t('telehealth-core:troubleshooting.cameraOn')
                    : t('telehealth-core:troubleshooting.cameraOff')}
                </Body.Default>
              </div>
              <div className='flex flex-row items-center gap-4'>
                <AudioControl />
                <Body.Default className={cn({ 'rebrand:text-text-inverse': isDarkTheme })}>
                  {isAudioEnabled
                    ? t('telehealth-core:troubleshooting.microphoneOn')
                    : t('telehealth-core:troubleshooting.microphoneOff')}
                </Body.Default>
              </div>
            </div>
            <Body.Xsmall className={cn('text-xsmall text-neutral-700', { 'rebrand:text-text-inverse': isDarkTheme })}>
              <Translate
                i18nKey='telehealth-core:troubleshooting.ifYoureExperiencingCameraOrMicrophoneIssues'
                components={{
                  1: (
                    <Link
                      size='xs'
                      external
                      href={CLIENT_AUDIO_VIDEO_TROUBLESHOOT_ARTICLE}
                      className={cn('text-neutral-700 hover:text-neutral-900 text-xsmall font-normal', {
                        'rebrand:text-action-primary-textLinkInverse rebrand:hover:text-action-primary-textLinkInverseHover':
                          isDarkTheme,
                      })}
                    />
                  ),
                }}
              />
            </Body.Xsmall>
          </div>
        }
      />
      <HelpModalItem
        heading={t('telehealth-core:troubleshooting.myVideoOrAudioQualityIsPoor')}
        description={
          <>
            {t('telehealth-core:troubleshooting.connectivityIssuesMayAffectYourVideoOrAudioQuality')}
            <ul className='list-disc ml-4'>
              <li>{t('telehealth-core:troubleshooting.turnYourVideoBackgroundOffIfEnabled')}</li>
              <li>{t('telehealth-core:troubleshooting.turnYourCameraOff')}</li>
              {shouldShowVideoQualitySettings && (
                <li>
                  <Translate
                    i18nKey='telehealth-core:troubleshooting.inSettingsChangeYourVideoQualityToOptimizeForPerformance'
                    components={{
                      1: (
                        <Button
                          inline
                          className={cn('font-normal text-inherit', {
                            'rebrand:text-action-primary-textLinkInverse rebrand:hover:text-action-primary-textLinkInverseHover':
                              isDarkTheme,
                          })}
                          use='link'
                          onClick={() => {
                            onOpenSettings?.();
                          }}
                        />
                      ),
                    }}
                  />
                </li>
              )}
              <li>{t('telehealth-core:troubleshooting.getCloserToTheWifiRouter')}</li>
              <li>{t('telehealth-core:troubleshooting.closeBrowserTabsThatArentInUse')}</li>
              <li>{t('telehealth-core:troubleshooting.ensureYourDeviceHasAGoodBatteryLevel')}</li>
            </ul>
          </>
        }
      />
    </>
  );
}

export function AudioVideoTroubleshootActionItemWrapper(
  props: Pick<AudioVideoTroubleshootActionItemProps, 'onOpenSettings'>,
) {
  const { theme } = useTheme();
  const blockedPermissions = useAtomValue(permissionDeniedAtom);
  const { videoIsBlocked, microphoneIsBlocked } = blockedPermissions;
  const videoPlatformIsZoom = useVideoPlatform() === VideoPlatform.Zoom;

  const { delegator: localVideoDelegator, status: localVideoStatus } = useAtomValue(localVideoContextAtom);
  const { delegator: localAudioDelegator, status: localAudioStatus } = useAtomValue(localAudioContextAtom);
  const isVideoEnabled = localVideoDelegator && localVideoStatus === LocalMediaStatus.READY;
  const isAudioEnabled = localAudioDelegator && localAudioStatus === LocalMediaStatus.READY;

  return (
    <AudioVideoTroubleshootActionItem
      {...props}
      isDarkTheme={theme === Theme.DARK}
      permissionIsBlocked={videoIsBlocked || microphoneIsBlocked}
      isVideoEnabled={isVideoEnabled}
      isAudioEnabled={isAudioEnabled}
      shouldShowVideoQualitySettings={!videoPlatformIsZoom}
    />
  );
}
